import { AxiosError, AxiosResponse, CanceledError, InternalAxiosRequestConfig } from 'axios';

import { CompanyErrorBody } from '@/api/pc-api/v1/company';
import { router } from '@/App';
import { ServerActionPopupErrorStatus, ServerPageErrorStatus } from '@/constants/error/serverErrorStatus';
import { ROUTES } from '@/constants/routes';
import { useAuthStore } from '@/store/auth';
import HttpStatusError from '@/utils/error/HttpStatusError';
import LoungeApiError from '@/utils/error/LoungeApiError';
import ServerActionPopupError from '@/utils/error/ServerActionPopupError';
import { handlePopupError, handleServerAuthError, handleServerPageError } from '@/utils/globalErrorHandler';
import { setSentryLevelToWarning } from '@/utils/matrix';
import { convertKeysToCamelCase, convertKeysToSnakeCase } from '@/utils/string';

const SERVER_ERROR_CODE = 500;

export const PREFIX_PATH = '{authInfoId}';

export const requestHandler = (config: InternalAxiosRequestConfig) => {
  if (!config.url) return config;

  if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
    config.data = convertKeysToSnakeCase(config.data);
  }

  const { authInfoId } = useAuthStore.getState();
  const regex = new RegExp(PREFIX_PATH, 'g');

  if (regex.test(config.url)) {
    config.url = config.url.replace(regex, authInfoId);
  }

  return config;
};

export const responseHandler = (response: AxiosResponse) => {
  if (response.data) {
    if (response.data.data && typeof response.data.data === 'object')
      response.data.data = convertKeysToCamelCase(response.data.data);

    if ('total_page_number' in response.data) response.data.totalPageNumber = response.data.total_page_number;
    if ('total_elements_count' in response.data) response.data.totalElementsCount = response.data.total_elements_count;
  }
  return response;
};

export const responseErrorHandler = (errorResponse: AxiosResponse) => {
  if (errorResponse instanceof AxiosError) {
    // axios 요청 취소 (ex. cancelToken)
    if (errorResponse.code === AxiosError.ERR_CANCELED) {
      throw new CanceledError();
    }

    // axios의 네트워크 관련 문제
    if (errorResponse.code === AxiosError.ERR_NETWORK) {
      setSentryLevelToWarning();
      router.navigate(ROUTES.ERROR.INTERNAL, { replace: true, state: { returnUrl: location.href } });
      (errorResponse as AxiosError)._isHandled = true;
      return Promise.reject(errorResponse);
    }

    // 500 미만 에러는 warning으로 처리하여 알람이 울리지 않도록 한다.
    const status = errorResponse.response?.status;
    if (status !== undefined && status < SERVER_ERROR_CODE) {
      setSentryLevelToWarning();
    }

    const errorResponseBody = errorResponse.response?.data;

    const loungeApiError = LoungeApiError.fromAxiosError(errorResponse);
    if (loungeApiError) {
      // 라운지 에러는 매트릭스에서 잡는다.
      return Promise.reject(loungeApiError);
    }

    // 인증 에러
    if (HttpStatusError.isServerAuthError(errorResponseBody)) {
      handleServerAuthError(router, errorResponseBody.status);
      (errorResponse as AxiosError)._isHandled = true;
      return Promise.reject(errorResponse);
    }

    if (HttpStatusError.isServerPageError(errorResponseBody)) {
      handleServerPageError(router, errorResponseBody.status);
      (errorResponse as AxiosError)._isHandled = true;
      return Promise.reject(errorResponse);
    }

    if (HttpStatusError.isServerPopupError(errorResponseBody)) {
      handlePopupError(errorResponseBody.status);
      (errorResponse as AxiosError)._isHandled = true;
      return Promise.reject(errorResponse);
    }

    // Action Popup, Popup 이후 추가 동작이 필요한 경우
    if (HttpStatusError.isServerActionPopupError(errorResponseBody)) {
      if (errorResponseBody.status === ServerActionPopupErrorStatus.NOT_FULL_INFO_COMPANY) {
        throw new ServerActionPopupError({
          message: errorResponseBody.message,
          status: errorResponseBody.status,
          data: convertKeysToCamelCase(errorResponseBody.data) as CompanyErrorBody
        });
      }

      throw new ServerActionPopupError({
        message: errorResponseBody.message,
        status: errorResponseBody.status
      });
    }
  }

  handleServerPageError(router, ServerPageErrorStatus.UNDEFINED);
  return Promise.reject(errorResponse);
};
